import { gameArea } from "../utils/gameArea";
import DigitalWarriorAI from "../managers/enemyAI/DigitalWarriorAI";
import SoundManager from "../managers/SoundManager";
import BaseCombatant from "./BaseCombatant";
import GameScene from "../scenes/GameScene";

export enum WarriorActions {
    IDLE = 0,
    MOVING = 1,
    ATTACKING = 2,
    DEFENDING = 3,
    /* AI SPECIFIC AFTER HERE */
    HESITATING = 4
};

export enum WarriorStates {
    NORMAL = 0,
    HIT = 1,
    DEAD = 2
}

export class AttackCircle extends Phaser.GameObjects.Arc {
    parent: DigitalWarrior;
}

export default class DigitalWarrior extends BaseCombatant {

    brain: DigitalWarriorAI | null;

    currentAction: WarriorActions;
    currentState: WarriorStates;

    attackCircle: AttackCircle;
    attackCircleBody: Phaser.Physics.Arcade.Body;
    attackSwingTween: Phaser.Tweens.Tween;
    attackChargeMax: number;
    attackCharge: number;
    attackCost: number;
    hitSound: string;
    hitSoundRate: number;

    constructor(scene: GameScene, x: number,y: number,name: string,spritesheet: string){
        super(scene,x,y,name);

        this.attackCircle = <AttackCircle>scene.add.circle(this.x,this.y,4*gameArea.scale,0x000000,0);
        scene.attackCircles.add(this.attackCircle);
        this.attackCircle.parent = this;
        scene.physics.add.existing(this.attackCircle);
        this.attackCircleBody = <Phaser.Physics.Arcade.Body>this.attackCircle.body;
        this.attackCircleBody.setCircle(1);
        this.attackCircleBody.setImmovable(true);
        this.attackCircle.setOrigin(0);

        this.scene.anims.create({
            'key': `${this.name}IdleDown`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [0,4,0,4,4,0] }),
            'repeat': -1,
            'delay': 5000,
            'repeatDelay': 7000
        });

        this.scene.anims.create({
            'key': `${this.name}IdleUp`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [4] }),
            'repeat': -1,
            'delay': 5000,
            'repeatDelay': 7000
        });

        this.scene.anims.create({
            'key': `${this.name}MovingDown`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [0,1,2,3] }),
            'repeat': -1
        });

        this.scene.anims.create({
            'key': `${this.name}MovingUp`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [4,5,6,7] }),
            'repeat': -1
        });

        this.scene.anims.create({
            'key': `${this.name}AttackDown`,
            'frameRate': 10,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [8,9,10,11,12] }),
            'repeat': 0
        });

        this.scene.anims.create({
            'key': `${this.name}AttackUp`,
            'frameRate': 10,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [13,14,15,16,17] }),
            'repeat': 0
        });

        this.scene.anims.create({
            'key': `${this.name}DefendDown`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [18,19,18,19,19,18] }),
            'repeat': -1,
            'delay': 5000,
            'repeatDelay': 7000
        });

        this.scene.anims.create({
            'key': `${this.name}DefendUp`,
            'frameRate': 6,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [19] }),
            'repeat': -1
        });

        this.scene.anims.create({
            'key': `${this.name}Dead`,
            'frameRate': 20,
            'frames': this.scene.anims.generateFrameNumbers(spritesheet, { frames: [20,21,22,23,24,25,26,27,28,29,30,31,32,33] }),
            'repeat': 0
        });

        this.anims.play(`${this.name}IdleDown`);


        this.attackChargeMax = 100;
        this.attackCharge = this.attackChargeMax;
        this.attackCost = 25;

        this.currentAction = WarriorActions.IDLE;

        this.setState(WarriorStates.NORMAL);

        this.brain = null;
    }

    isAttacking(){
        return this.currentAction === WarriorActions.ATTACKING;
    }

    canBeHit(){
          return (
            this.currentAction !== WarriorActions.DEFENDING &&
            this.currentState === WarriorStates.NORMAL
          )
    }

    takeHit(direction: Phaser.Math.Vector2,damage: number = 10){
        this.health -= damage;
        this.resetAttackCircle();
        if(this.health <= 0){
            this.stop();
            this.health = 0;
            this.setState(WarriorStates.DEAD);
            return;
        }
        this.scene.time.delayedCall(200, () =>{
            SoundManager.playSfx(this.hitSound,1, this.hitSoundRate + (Math.random()/5))
        })
        this.setAction(WarriorActions.IDLE);
        const randomize = new Phaser.Math.Vector2( (50 * Math.random()) - 25, (50 * Math.random()) - 25 );
        this.setVelocity((direction.x * this.speed * 4) + randomize.x,(direction.y * this.speed * 4) + randomize.y);
        this.setMaxVelocity(this.speed * 5,this.speed * 5);
        this.setState(WarriorStates.HIT);
    }

    move(direction: Phaser.Math.Vector2){
        if(
            (direction.x === this.prevDirection.x && direction.y === this.prevDirection.y) ||
            this.currentAction === WarriorActions.ATTACKING ||
            this.currentAction === WarriorActions.DEFENDING ||
            this.currentState === WarriorStates.HIT ||
            this.currentState === WarriorStates.DEAD
        ) return;
        if(direction.x === 1 || direction.x === -1){
            this.directionFacing.x = direction.x;
        }
        if(direction.y === 1 || direction.y === -1){
            this.directionFacing.y = direction.y;
        }
        if(direction.y === 0 && direction.x === 0){
            this.setAction(WarriorActions.IDLE);
        }else{
            this.setAction(WarriorActions.MOVING);
        }
        this.prevDirection = direction;
        this.setAcceleration(direction.x * this.speed * 5, direction.y * this.speed * 5);
    }

    setState(newState: WarriorStates){
        switch(newState){
            case WarriorStates.NORMAL:
                this.stop();
                this.setAlpha(1);
                this.currentState = newState;
                this.setAction(WarriorActions.IDLE);
                this.setFriction(this.speed * 500);
                this.setDrag(this.speed * 5);
                this.setMaxVelocity(this.speed,this.speed);
                this.shadow.setVisible(true);
                this.body.enable = true;
            break;
            case WarriorStates.HIT:
                this.setAlpha(0.5);
                this.resetAttackCircle();
                this.setDrag(1);
                this.anims.play( this.directionFacing.y === -1 ? `${this.name}DefendUp` : `${this.name}DefendDown`);
                this.scene.time.delayedCall(500, ()=> { this.setState(WarriorStates.NORMAL) });
            break;
            case WarriorStates.DEAD:
                SoundManager.playSfx('death', this.hitSoundRate + (Math.random()/5));
                this.anims.play(`${this.name}Dead`);
                this.shadow.setVisible(false);
                this.body.enable = false;
            break;
        }
        this.currentState = newState;
        return this;
    }

    resetAttackCircle(){
        if(this.attackSwingTween instanceof Phaser.Tweens.Tween)
            this.attackSwingTween.stop();
        this.attackCircleBody.setCircle(0.1);
        this.attackCircle.setOrigin(0.5);
        this.attackCircle.setPosition(-1000, -1000);
    }

    setAction(newAction: WarriorActions){
        if(this.currentState === WarriorStates.HIT || this.currentState === WarriorStates.DEAD) return;
        switch(newAction){
            case WarriorActions.IDLE:
                this.prevDirection.x = 0;
                this.prevDirection.y = 0;
                if(this.directionFacing.y === -1)
                    this.anims.play(`${this.name}IdleUp`)
                if(this.directionFacing.y === 1)
                    this.anims.play(`${this.name}IdleDown`)
                if(this.directionFacing.x === -1)
                    this.setFlipX(true);
                if(this.directionFacing.x === 1)
                    this.setFlipX(false);
                this.currentAction = newAction;
            break;
            case WarriorActions.MOVING:
                if(this.directionFacing.y === -1)
                    this.anims.play(`${this.name}MovingUp`)
                if(this.directionFacing.y === 1)
                    this.anims.play(`${this.name}MovingDown`)
                if(this.directionFacing.x === -1)
                    this.setFlipX(true);
                if(this.directionFacing.x === 1)
                    this.setFlipX(false);
                this.currentAction = newAction;
            break;
            case WarriorActions.ATTACKING:
                if(!this.canAttack()) return;
                this.stop();
                this.attackCircle.setOrigin(0.5)
                this.attackCircleBody.setMass(100);
                const swingStart = new Phaser.Math.Vector2(this.x + ((8*gameArea.scale)*this.directionFacing.x), this.y );
                const swingEnd = new Phaser.Math.Vector2(this.x, this.y + ((8*gameArea.scale)*this.directionFacing.y) );
                if(this.directionFacing.y === -1)
                    this.anims.play(`${this.name}AttackUp`);
                if(this.directionFacing.y === 1)
                    this.anims.play(`${this.name}AttackDown`);
                this.attackSwingTween = this.scene.add.tween({
                    delay: 200,
                    duration: 300,
                    onStart: ()=> {
                        SoundManager.playSfx("swing",1,0.9 + (Math.random()/5));
                        this.attackCharge -= this.attackCost;
                        this.attackCircleBody.setCircle(4*gameArea.scale);
                    },
                    onComplete: ()=> {
                        this.resetAttackCircle();
                        this.setAction(WarriorActions.IDLE);
                    },
                    targets: this.attackCircle,
                    x: {
                        from: swingStart.x,
                        to: swingEnd.x
                    },
                    y: {
                        from: swingStart.y,
                        to: swingEnd.y
                    }
                })
                this.currentAction = newAction;
            break;
            case WarriorActions.DEFENDING:
                if(this.currentAction === WarriorActions.ATTACKING || this.currentAction === WarriorActions.DEFENDING) return;
                this.stop();
                if(this.directionFacing.y === -1)
                    this.anims.play(`${this.name}DefendUp`);
                if(this.directionFacing.y === 1)
                    this.anims.play(`${this.name}DefendDown`);
                this.currentAction = newAction;
            break;
        }
    }

    canAttack(){
        return (this.currentAction !== WarriorActions.ATTACKING &&
        this.currentAction !== WarriorActions.DEFENDING &&
        this.attackCharge >= this.attackCost);
    }


    update(time: number, delta: number){
        if(this.attackCharge < this.attackChargeMax){
            this.addCharge(delta/500);
        }
        if(this.brain !== null) this.brain.update(time, delta);
    }

    addCharge(additionalCharge: number){
        this.attackCharge += additionalCharge;
        if(this.attackCharge > this.attackChargeMax) this.attackCharge = this.attackChargeMax;
    }

    reset(){
        super.reset();
        this.hitSound = `hit${Math.ceil(Math.random() * 2)}`;
        this.hitSoundRate = 0.8 + Math.random()/5;
        this.attackCharge = this.attackChargeMax;
        this.setState(WarriorStates.NORMAL);
        if(this.brain !== null) this.brain.reset();
    }

    destroy(){
        this.shadow.destroy(true);
        const scene = <GameScene>this.scene;
        scene.attackCircles.remove(this.attackCircle,true,true);
        super.destroy();
    }

}